import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { commandCenterApi } from '../../utils/api';
import StatInfo from '../../components/StatInfo';
import { toast } from '../../components/Toast';
import { LoadState, formatDate, useRefetchOnFocus } from './parts';
import { Dash, Metric, SortTh, fmt } from './statsParts';
import SupplierDetailModal from './SupplierDetailModal';
import SupplierIdsSection from './SupplierIdsSection';
import SuspectReview from './SuspectReview';

// סטטיסטיקות → ספקים — groundwork for one supplier entity across the
// platform (lib/ccSupplierStats.js). Three questions, top to bottom:
//   how many supplier instances do we hold, and how many suppliers is that?
//   which suppliers look like the same one, and why?
//   per supplier, what does each restaurant have configured, and pay?
// The current state, not a period — so the page's date filter is hidden here.

const PAGE = 50;

const CONFIDENCE = {
  high:   { label: 'גבוה',  pill: 'bad' },
  medium: { label: 'בינוני', pill: 'warn' },
  low:    { label: 'נמוך',  pill: '' },
};

// Why the system suspects two suppliers are one. Wording matches the server's
// five signals (lib/ccSupplierStats.js#suspectedPairs).
const REASONS = {
  business_id: { label: 'אותו ח.פ.', help: 'לשני הספקים אותו ח.פ. — שהוזן ידנית, או שמודפס על המסמכים שלהם. זו זהות הספק עצמה, לא רק דמיון (ח.פ. של אחת המסעדות שלנו לא נחשב).' },
  spelling:  { label: 'אותו שם בכתיב שונה', help: 'השמות זהים כשמתעלמים מסימני פיסוק, רווחים וסיומות כמו בע״מ.' },
  typo:      { label: 'הפרש של אות אחת',    help: 'השמות שונים באות אחת בלבד — בדרך כלל שגיאת הקלדה.' },
  catalog:   { label: 'מוצרים משותפים',     help: 'שני הספקים מוכרים את אותם מוצרים (לפי מק״ט, או שם כשאין מק״ט).' },
  contained: { label: 'שם אחד כלול בשני',   help: 'השם הקצר מופיע בתוך הארוך — לרוב סניף, מותג או תוספת לשם.' },
};

// ─── Load ────────────────────────────────────────────────────────────────────

function useSupplierStats() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commandCenterApi.getSupplierStats();
      setData(r.data);
    } catch {
      setError(true);
      setData(null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefetchOnFocus(load);
  return { data, error, load };
}

// ─── Metrics ─────────────────────────────────────────────────────────────────

function SupplierMetrics({ totals }) {
  return (
    <div className="cc-cardmetrics">
      <Metric label="מופעי ספקים" value={fmt(totals.instances)} detail={`ספק אצל מסעדה · ${fmt(totals.restaurants)} מסעדות`} />
      <Metric label="ספקים" value={fmt(totals.suppliers)} detail="אותו שם בכל המסעדות = ספק אחד" />
      <Metric label="אצל יותר ממסעדה אחת" value={fmt(totals.multi_restaurant)} />
      <Metric label="הגדרות שונות בין מסעדות" value={fmt(totals.with_differences)} />
      <Metric label="חשודים ככפולים" value={fmt(totals.suspected_pairs)} detail={`${fmt(totals.suspected_high)} בביטחון גבוה`}
        tone={totals.suspected_pairs ? 'alert' : 'good'} />
      <Metric label="כפולים באותה מסעדה" value={fmt(totals.same_restaurant)} detail="ניתן למזג כבר היום"
        tone={totals.same_restaurant ? 'alert' : 'good'} />
      <Metric label="עם ח.פ." value={fmt(totals.with_business_id)}
        detail="הוזן ידנית, או נקרא מהמסמכים (נאסף מ-27/09/2026)" />
    </div>
  );
}

// ─── Suspected duplicates ────────────────────────────────────────────────────

function ReasonChip({ reason }) {
  const meta = REASONS[reason.code];
  if (reason.code === 'business_id') {
    return <span className="cc-badge cc-reason" title={meta.help}>{meta.label} {reason.value}</span>;
  }
  if (reason.code !== 'catalog') return <span className="cc-badge cc-reason" title={meta.help}>{meta.label}</span>;
  return (
    <span className="cc-badge cc-reason cc-types">
      {meta.label} {fmt(reason.shared)}/{fmt(reason.of)}
      <StatInfo label={meta.label} ariaLabel="דוגמאות למוצרים משותפים" maxWidth={260}>
        <div className="cc-types-title">{meta.help}</div>
        <ul className="stat-info-list">{reason.examples.map(e => <li key={e}>{e}</li>)}</ul>
      </StatInfo>
    </span>
  );
}

function SupplierLink({ s, onOpen }) {
  return (
    <button type="button" className="cc-linkbtn" onClick={() => onOpen(s)}>
      {s.name}
      <span className="cc-muted" style={{ fontWeight: 400 }}> · {fmt(s.restaurants)} מסעדות</span>
    </button>
  );
}

const pairKeyOf = (p) => `${p.a.key}|${p.b.key}`;

function SuspectRow({ p, open, onToggle, onOpen }) {
  const conf = CONFIDENCE[p.confidence];
  return (
    <tr className={open ? 'is-open' : undefined}>
      <td><span className={`cc-pill ${conf.pill}`}>{conf.label}</span></td>
      <td><SupplierLink s={p.a} onOpen={onOpen} /></td>
      <td><SupplierLink s={p.b} onOpen={onOpen} /></td>
      <td><span className="cc-reasons">{p.reasons.map(r => <ReasonChip key={r.code} reason={r} />)}</span></td>
      <td>
        {p.same_restaurants.length
          ? <>{p.same_restaurants.map(r => r.name).join(', ')} <span className="cc-pill warn">נדרש מיזוג</span></>
          : <Dash />}
      </td>
      <td>
        <button type="button" className="btn btn-sm btn-outline" aria-expanded={open} onClick={onToggle}>
          {open ? 'סגור' : 'להחלטה'}
        </button>
      </td>
    </tr>
  );
}

function ConfidenceFilter({ value, onChange, suspects }) {
  const count = (c) => (c === 'all' ? suspects.length : suspects.filter(p => p.confidence === c).length);
  return (
    <span className="cc-segment" role="group" aria-label="סינון לפי ביטחון">
      {['all', 'high', 'medium', 'low'].map(c => (
        <button key={c} type="button" className={value === c ? 'is-active' : undefined} onClick={() => onChange(c)}>
          {c === 'all' ? 'הכל' : CONFIDENCE[c].label} ({fmt(count(c))})
        </button>
      ))}
    </span>
  );
}

function PairsTable({ rows, openIndex, setOpenIndex, onOpen, onDecided }) {
  return (
    <div className="cc-suspects-scroll">
      <table className="cc-list">
        <thead>
          <tr><th>ביטחון</th><th>ספק</th><th>ספק דומה</th><th>למה</th><th>אצל אותה מסעדה</th><th /></tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <Fragment key={pairKeyOf(p)}>
              <SuspectRow p={p} open={openIndex === i} onOpen={onOpen} onToggle={() => setOpenIndex(openIndex === i ? null : i)} />
              {openIndex === i && (
                <tr className="cc-review-row">
                  <td colSpan={6}>
                    {/* A decision re-reads the list: the decided pair leaves it and the next one
                        moves into this position, so the index stays — by position, not by key,
                        since a rename changes the keys of the cluster's other pairs. */}
                    <SuspectReview pair={p} onDone={onDecided} onSkip={() => setOpenIndex(i + 1 < rows.length ? i + 1 : null)} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The pending pairs, easiest first (the server's order). A pair opens in place
// with the evidence and the decision; deciding opens the next one, so a run
// through the list is one or two clicks a pair.
function PendingPairs({ suspects, onOpen, onDecided }) {
  const [confidence, setConfidence] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);
  const rows = confidence === 'all' ? suspects : suspects.filter(p => p.confidence === confidence);
  const visible = showAll ? rows : rows.slice(0, PAGE);
  const filter = (c) => { setConfidence(c); setOpenIndex(null); };
  return (
    <>
      <div className="cc-card-head" style={{ flexWrap: 'wrap', marginTop: 0 }}>
        <span className="cc-spacer" />
        <ConfidenceFilter value={confidence} onChange={filter} suspects={suspects} />
      </div>
      {rows.length === 0
        ? <div className="cc-empty">{suspects.length ? 'אין חשודים ברמת ביטחון זו' : 'אין זוגות שממתינים להחלטה'}</div>
        : <PairsTable rows={visible} openIndex={openIndex} setOpenIndex={setOpenIndex} onOpen={onOpen} onDecided={onDecided} />}
      {!showAll && rows.length > PAGE && (
        <button className="btn btn-sm btn-secondary" style={{ marginTop: 10 }} onClick={() => setShowAll(true)}>
          הצג את כל {fmt(rows.length)} הזוגות
        </button>
      )}
    </>
  );
}

function decisionOutcome(d) {
  if (d.decision === 'different') return 'לא אותו ספק';
  const where = d.restaurants.length ? ` · ${d.restaurants.join(', ')}` : '';
  return `אוחד בשם "${d.chosen_name}" · ${fmt(d.renamed)} שינויים${where}`;
}

function useDecisions() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await commandCenterApi.getSupplierDecisions();
      setRows(r.data.decisions);
    } catch {
      setError(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { rows, error, load };
}

function DecisionRow({ d, busy, onUndo }) {
  return (
    <tr className={d.undone_at ? 'is-undone' : undefined}>
      <td className="cc-num">{formatDate(d.decided_at)}</td>
      <td>{d.name_a} · {d.name_b}</td>
      <td>{decisionOutcome(d)}</td>
      <td className="cc-muted">{d.decided_by || '—'}</td>
      <td>
        {d.undone_at
          ? <span className="cc-muted">בוטל</span>
          : <button type="button" className="btn btn-sm btn-outline" disabled={busy} onClick={onUndo}>{busy ? 'מבטל…' : 'בטל'}</button>}
      </td>
    </tr>
  );
}

// The log of decisions, newest first, each undoable (lib/supplierIdentity.js).
function DecidedPairs({ onChanged }) {
  const { rows, error, load } = useDecisions();
  const [busyId, setBusyId] = useState(null);
  const undo = async (d) => {
    setBusyId(d.id);
    try {
      await commandCenterApi.undoSupplierDecision(d.id);
      toast('ההחלטה בוטלה');
      await load();
      onChanged();
    } catch (err) {
      toast(err.response?.data?.error || 'הביטול נכשל — אפשר לנסות שוב', 'error');
    } finally {
      setBusyId(null);
    }
  };
  return (
    <LoadState loading={!rows && !error} error={error} onRetry={load} empty={rows?.length === 0} emptyMessage="עוד לא התקבלו החלטות">
      {rows && (
        <div className="cc-suspects-scroll">
          <table className="cc-list">
            <thead><tr><th>מתי</th><th>ספקים</th><th>החלטה</th><th>מי</th><th /></tr></thead>
            <tbody>{rows.map(d => <DecisionRow key={d.id} d={d} busy={busyId === d.id} onUndo={() => undo(d)} />)}</tbody>
          </table>
        </div>
      )}
    </LoadState>
  );
}

function SuspectsSection({ suspects, decided, onOpen, onDecided }) {
  const [view, setView] = useState('pending');
  return (
    <section className="cc-tabpanel-section">
      <div className="cc-card-head" style={{ flexWrap: 'wrap' }}>
        <h2 className="cc-card-title">חשודים כאותו ספק</h2>
        <StatInfo label="חשודים כאותו ספק" ariaLabel="איך מזהים ספקים חשודים ככפולים" maxWidth={320}>
          <ul className="stat-info-list">
            {Object.values(REASONS).map(r => <li key={r.label}><b>{r.label}</b> — {r.help}</li>)}
            <li>שתי סיבות שונות לאותו זוג מעלות את רמת הביטחון.</li>
            <li>&quot;אותו ספק&quot; משנה את השם בכל המסעדות לשם שנבחר; כל השמות ושמות הכינוי של שניהם נשמרים כשמות כינוי, וח.פ. יחיד מועתק לרשומות שאין להן (ניתן לבטל ב&quot;הוחלטו&quot;). &quot;לא אותו ספק&quot; מסתיר את הזוג.</li>
          </ul>
        </StatInfo>
        <span className="cc-spacer" />
        <span className="cc-segment" role="group" aria-label="מצב החלטה">
          <button type="button" className={view === 'pending' ? 'is-active' : undefined} onClick={() => setView('pending')}>
            ממתינים ({fmt(suspects.length)})
          </button>
          <button type="button" className={view === 'decided' ? 'is-active' : undefined} onClick={() => setView('decided')}>
            הוחלטו ({fmt(decided)})
          </button>
        </span>
      </div>
      {view === 'pending'
        ? <PendingPairs suspects={suspects} onOpen={onOpen} onDecided={onDecided} />
        : <DecidedPairs onChanged={onDecided} />}
    </section>
  );
}

// ─── Supplier directory ──────────────────────────────────────────────────────

const sortValue = (s, col) => (col === 'differs' ? s.differs.length : s[col]);

function useDirectoryRows(suppliers, { q, multiOnly, diffOnly, sort }) {
  return useMemo(() => {
    const needle = q.trim().toLowerCase();
    return suppliers
      .filter(s => (!multiOnly || s.restaurants > 1) && (!diffOnly || s.differs.length)
        && (!needle || s.variants.some(v => v.toLowerCase().includes(needle))))
      .sort((a, b) => sortValue(b, sort) - sortValue(a, sort) || b.docs - a.docs);
  }, [suppliers, q, multiOnly, diffOnly, sort]);
}

function DirectoryRow({ s, labelOf, onOpen }) {
  return (
    <tr className="cc-row-link" onClick={() => onOpen(s)}>
      <td className="cc-matrix-name">
        {s.name}
        {s.duplicate_in_restaurant && <span className="cc-pill bad" style={{ marginInlineStart: 6 }}>כפול באותה מסעדה</span>}
      </td>
      {/* Instances = this supplier's rows across restaurants; hover names
          every spelling they are filed under. */}
      <td className="cc-num" title={`כתיבים: ${s.variants.join(' · ')}`}>{fmt(s.instances)}</td>
      <td className="cc-num">{fmt(s.restaurants)}</td>
      <td className="cc-num">{s.docs ? fmt(s.docs) : <Dash />}</td>
      <td>{s.differs.length ? s.differs.map(k => <span key={k} className="cc-badge cc-reason">{labelOf(k)}</span>) : <Dash />}</td>
      <td className="cc-num">{formatDate(s.last_doc)}</td>
    </tr>
  );
}

function DirectoryFilters({ q, setQ, multiOnly, setMultiOnly, diffOnly, setDiffOnly }) {
  return (
    <>
      <label className="cc-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <input type="checkbox" checked={multiOnly} onChange={e => setMultiOnly(e.target.checked)} />
        רק אצל כמה מסעדות
      </label>
      <label className="cc-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
        <input type="checkbox" checked={diffOnly} onChange={e => setDiffOnly(e.target.checked)} />
        רק עם הגדרות שונות
      </label>
      <input className="form-control" style={{ maxWidth: 200 }} value={q} onChange={e => setQ(e.target.value)}
        placeholder="חיפוש ספק…" aria-label="חיפוש ספק ברשימת הספקים" />
    </>
  );
}

function Directory({ suppliers, settings, onOpen }) {
  const [q, setQ] = useState('');
  const [multiOnly, setMultiOnly] = useState(true);
  const [diffOnly, setDiffOnly] = useState(false);
  const [sort, setSort] = useState('restaurants');
  const [showAll, setShowAll] = useState(false);
  const rows = useDirectoryRows(suppliers, { q, multiOnly, diffOnly, sort });
  const visible = showAll || q ? rows : rows.slice(0, PAGE);
  const labels = useMemo(() => new Map(settings.map(f => [f.key, f.label])), [settings]);

  return (
    <section className="cc-tabpanel-section">
      <div className="cc-card-head" style={{ flexWrap: 'wrap' }}>
        <h2 className="cc-card-title">ספקים והגדרות לפי מסעדה</h2>
        <span className="cc-muted" style={{ fontSize: '0.79rem' }}>לחיצה על ספק פותחת הגדרות ומחירים לכל מסעדה</span>
        <span className="cc-spacer" />
        <DirectoryFilters {...{ q, setQ, multiOnly, setMultiOnly, diffOnly, setDiffOnly }} />
      </div>
      {rows.length === 0 ? (
        <div className="cc-empty">אין ספקים שתואמים לסינון</div>
      ) : (
        <div className="cc-matrix-scroll">
          <table className="cc-list">
            <thead>
              <tr>
                <th className="cc-matrix-name">ספק</th>
                <SortTh col="instances" sort={sort} onSort={setSort}>מופעים</SortTh>
                <SortTh col="restaurants" sort={sort} onSort={setSort}>מסעדות</SortTh>
                <SortTh col="docs" sort={sort} onSort={setSort}>מסמכים</SortTh>
                <SortTh col="differs" sort={sort} onSort={setSort}>הגדרות שונות בין מסעדות</SortTh>
                <th>פעילות אחרונה</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(s => <DirectoryRow key={s.key} s={s} labelOf={k => labels.get(k) || k} onOpen={onOpen} />)}
            </tbody>
          </table>
        </div>
      )}
      {!q && !showAll && rows.length > PAGE && (
        <button className="btn btn-sm btn-secondary" style={{ marginTop: 10 }} onClick={() => setShowAll(true)}>
          הצג את כל {fmt(rows.length)} הספקים
        </button>
      )}
    </section>
  );
}

// ─── The view ────────────────────────────────────────────────────────────────

export default function SuppliersView() {
  const { data, error, load } = useSupplierStats();
  // A saved ח.פ. is identity for the matching, so the tab re-reads — once,
  // after a run of saves settles, not once per row: entering a list of IDs
  // back to back would otherwise recompute the whole payload each time.
  const reloadTimer = useRef(null);
  const reloadSoon = useCallback(() => {
    clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(load, 1500);
  }, [load]);
  useEffect(() => () => clearTimeout(reloadTimer.current), []);
  const [open, setOpen] = useState(null);   // { key, name } of the supplier in the modal
  const onOpen = useCallback((s) => setOpen({ key: s.key, name: s.name }), []);

  return (
    <LoadState loading={!data && !error} error={error} onRetry={load}
      empty={data?.totals.instances === 0} emptyMessage="אין ספקים במערכת">
      {data && (
        <>
          <SupplierMetrics totals={data.totals} />
          <SupplierIdsSection onSaved={reloadSoon} />
          <SuspectsSection suspects={data.suspects} decided={data.totals.decided || 0} onOpen={onOpen} onDecided={load} />
          <Directory suppliers={data.suppliers} settings={data.settings} onOpen={onOpen} />
          {open && (
            <SupplierDetailModal supplierKey={open.key} name={open.name} settings={data.settings}
              onClose={() => setOpen(null)} />
          )}
        </>
      )}
    </LoadState>
  );
}
